// vue
import { useStore } from '@/store';
import { useRouter } from '@/router';

// hooks
import { useAxios } from '@/vue3/hooks/axiosHook';

// services
import { usePermissionService } from '@/vue3/api/services/permissionService';
import { useOrganizationService } from '@/vue3/api/services/organizationService';
import { useCourseSchemaService } from '@/vue3/api/services/courseSchemaService';
import { useAccountService } from '@/vue3/api/services/accountService';

// models
import { AuthenticationCredentials, Version } from '@/vue3/api/models';

export const useAuthService = () => {
    // hooks
    const store = useStore();
    const router = useRouter();
    const axios = useAxios();

    // services
    const { getAllPermissionNameByAccountId } = usePermissionService();
    const { getOrganization } = useOrganizationService();
    const { getCourseSchemaByRole } = useCourseSchemaService();
    const { getAccount } = useAccountService();

    // properties
    const url = store.state.apiUrl;

    // methods
    const clearState = () => {
        store.state.authToken = null;
        store.state.accountPermissions = null;
        store.state.currentCourseSchema = null;
        store.state.currentOrganization = null;
        store.state.courseSchemas = null;
        store.state.account = null;
        store.state.tempAuthToken = null;
        store.state.masqueradeAccountPermissions = null;
        localStorage.removeItem('authToken');
        localStorage.removeItem('accountPermissions');
        localStorage.removeItem('currentCourseSchema');
        localStorage.removeItem('currentOrganization');
        localStorage.removeItem('courseSchemas');
        localStorage.removeItem('account');
        localStorage.removeItem('tempAuthToken');
        localStorage.removeItem('masqueradeAccountPermissions');
    };

    const clearCourseSchemaState = () => {
        store.state.currentCourseSchema = null;
        store.state.courseSchemas = null;
        localStorage.removeItem('currentCourseSchema');
        localStorage.removeItem('courseSchemas');
    };

    const getToken = async (
        authenticationCredentials: AuthenticationCredentials,
    ) => {
        const res = await axios.post(`${url}/auth`, authenticationCredentials);
        return res.data;
    };

    const getTempToken = async (accountId: number) => {
        const res = await axios.get(`${url}/auth/temp/${accountId}`);
        return res.data;
    };

    const changePassword = async (changePassword: any) => {
        const res = await axios.post(
            `${url}/auth/change-password`,
            changePassword,
        );
        if(res.status == 200 || res.status == 201){
            return true;
        }
        return false;
    };

    const resetPassword = async (resetPassword: any) => {
        const res = await axios.post(`${url}/reset-password`, resetPassword);
        return res.data;
    };

    const resetPasswordChange = async (resetPasswordChange: any) => {
        const res = await axios.post(
            `${url}/reset-password/change`,
            resetPasswordChange,
        );
        return res.data;
    };

    const addState = (field: string, value: any) => {
        store.state[field] = JSON.stringify(value);
        localStorage.setItem(field, JSON.stringify(value));
    };

    const initializeUserState = async (token: any) => {
        const p1 = getAllPermissionNameByAccountId(token.accountId);
        const p2 = getOrganization(token.organizationId);
        const p3 = getAccount(token.accountId);

        const accountPermissions = await p1;
        const currentOrganization = await p2;
        const account = await p3;

        const courseSchemas = await getCourseSchemaByRole(account.roleId);

        addState('accountPermissions', accountPermissions);
        addState('currentOrganization', currentOrganization);
        addState('account', account);

        addState('courseSchemas', courseSchemas);
        if (!localStorage.getItem('currentCourseSchema')) {
            if (courseSchemas.length > 0) {
                addState('currentCourseSchema', courseSchemas[0]);
            }
        }
    }

    const logIn = async (
        authenticationCredentials: AuthenticationCredentials,
    ) => {
        clearState();
        const token = await getToken(authenticationCredentials);
        addState('authToken', token);

        await initializeUserState(token)
    };

    const logOut = async () => {
        const res = await axios.deleteObject(
            `${url}/auth`,
            store.state.authToken,
        );
        clearState();
        clearMultiUserToken();

        router.push({ path: '/login' });
    };

    const getVersion = async (): Promise<Version> => {
        const res = await axios.get(`${url}/version`);
        return res.data;
    };

    const clearMultiUserToken = () => {
        localStorage.removeItem("multiUserAuthToken");  //Don't add this into clearState();
        store.state.multiUserAuthToken = null;          //Don't add this into clearState();
    }

    const getTokenAsMultiOrgUser = async (targetAccountId: number) => {
        try{
            const res = await axios.get(`${url}/auth/multi-org/${targetAccountId}`);
            return res.data;
        }
        catch(e){
            return null
        }
    };

    const logInToTargetAccountAsMultiOrgUser = async (targetAccountId: number) => {
        addState("multiUserAuthToken", JSON.parse(store.state.authToken || ""));
        const token = await getTokenAsMultiOrgUser(targetAccountId);
        clearState();
        addState('authToken', token);
        try {
            await initializeUserState(token);
            router.push({ path: '/course-manager' });
        } catch(e){
            console.log(e);
            logOut();
            return;
        }

    };

    const logOutFromTargetAccountAsMultiOrgUser = async () => {
        const res = await axios.deleteObject(
            `${url}/auth`,
            store.state.authToken,
        );
        clearCourseSchemaState();
        const token = JSON.parse(localStorage.getItem("multiUserAuthToken") || "{}");
        //check isExpired
        if(!token.accountId){
            logOut();
            return;
        }
        addState('authToken', token);
        try {
            await initializeUserState(token);
            clearMultiUserToken();
            window.location.reload()            
        } catch(e) {
            logOut();
            return;
        }
    };


    return {
        logIn,
        logOut,
        getVersion,
        resetPassword,
        changePassword,
        resetPasswordChange,
        getTempToken,
        addState,
        clearState,
        initializeUserState,
        getTokenAsMultiOrgUser,
        logInToTargetAccountAsMultiOrgUser,
        logOutFromTargetAccountAsMultiOrgUser
    };
};
